import React, { useState, useEffect } from 'react';
import { MessageSquare, Send, Smartphone, X, AlertCircle, Edit3 } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';

// Default message template
const DEFAULT_TEMPLATE = `Hi {{client_name}},

It's {{staff_name}} from Butler Direct — I just wanted to check in ahead of your upcoming {{job_type}}.

I'll be looking after you at {{event_address}} at {{event_time}}, and I wanted to introduce myself to make sure everything is running smoothly on your end.

If there's anything specific you'd like us to know or prepare for your {{job_type}}, or any questions you have beforehand, please don't hesitate to reach out. I'm here to help and ensure everything goes perfectly.

We'll make sure your {{job_type}} runs seamlessly, and I'm really looking forward to meeting you and your group.

Many thanks,
{{staff_name}}`;

const MessageClientButton = ({ job, user, shouldShowClientDetails, compact = false, fullWidth = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [missingFields, setMissingFields] = useState([]);
  const [sendMethod, setSendMethod] = useState('sms'); // 'sms' or 'whatsapp'

  // Check if we can show client details (privacy check)
  const canShowDetails = shouldShowClientDetails ? shouldShowClientDetails(job) : true;

  // Extract client first name
  const getClientFirstName = (fullName) => {
    if (!fullName) return '';
    return fullName.split(' ')[0];
  };

  // Format time for display
  const formatEventTime = (time) => {
    if (!time) return '';
    // Try to parse and format nicely
    try {
      // If it's already formatted like "6:00 PM", return as is
      if (time.includes('AM') || time.includes('PM') || time.includes('am') || time.includes('pm')) {
        return time;
      }
      // If it's 24h format like "18:00", convert
      const [hours, minutes] = time.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      return `${hour12}:${minutes} ${ampm}`;
    } catch {
      return time;
    }
  };

  // Build the message with dynamic fields
  const buildMessage = () => {
    const missing = [];
    
    // Check required fields
    if (!job.client_name) missing.push('Client name');
    if (!user?.name) missing.push('Staff name');
    if (!job.location) missing.push('Event address');
    if (!job.time) missing.push('Event time');
    if (!job.job_type) missing.push('Job type');
    if (!canShowDetails) missing.push('Client details (available 1 week before event)');

    setMissingFields(missing);

    if (missing.length > 0) {
      return '';
    }

    // Replace placeholders
    let msg = DEFAULT_TEMPLATE;
    msg = msg.replace(/\{\{client_name\}\}/g, getClientFirstName(job.client_name));
    msg = msg.replace(/\{\{staff_name\}\}/g, user?.name || '');
    msg = msg.replace(/\{\{event_address\}\}/g, job.location || '');
    msg = msg.replace(/\{\{event_time\}\}/g, formatEventTime(job.time));
    msg = msg.replace(/\{\{job_type\}\}/g, job.job_type?.toLowerCase() || '');

    return msg;
  };

  // Initialize message when dialog opens
  useEffect(() => {
    if (isOpen) {
      const builtMessage = buildMessage();
      setMessage(builtMessage);
    }
  }, [isOpen, job, user]);

  // Format phone number for messaging
  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    // Remove all non-numeric characters except +
    let cleaned = phone.replace(/[^\d+]/g, '');
    
    // If it starts with 0, assume UK and convert to +44
    if (cleaned.startsWith('0')) {
      cleaned = '+44' + cleaned.substring(1);
    }
    // If no country code, assume UK
    if (!cleaned.startsWith('+')) {
      cleaned = '+44' + cleaned;
    }
    return cleaned;
  };

  // Send via SMS
  const sendViaSMS = () => {
    const phone = formatPhoneNumber(job.client_phone);
    const encodedMessage = encodeURIComponent(message);
    
    // Use sms: URL scheme - works on both iOS and Android
    const smsUrl = `sms:${phone}?body=${encodedMessage}`;
    window.location.href = smsUrl;
    setIsOpen(false);
  };

  // Send via WhatsApp
  const sendViaWhatsApp = () => {
    const phone = formatPhoneNumber(job.client_phone).replace('+', '');
    const encodedMessage = encodeURIComponent(message);
    
    // Use WhatsApp URL scheme
    const waUrl = `https://wa.me/${phone}?text=${encodedMessage}`;
    window.open(waUrl, '_blank');
    setIsOpen(false);
  };

  // Check if button should be disabled
  const isDisabled = !canShowDetails || !job.client_phone;

  // Get tooltip/error message
  const getDisabledReason = () => {
    if (!canShowDetails) {
      return 'Client details available 1 week before event';
    }
    if (!job.client_phone) {
      return 'Client phone number not available';
    }
    return '';
  };

  return (
    <>
      {/* Message Client Button */}
      <Button
        onClick={() => setIsOpen(true)}
        disabled={isDisabled}
        variant="outline"
        size={compact ? "sm" : "default"}
        className={`${fullWidth ? 'w-full' : ''} ${compact ? 'text-xs' : 'text-sm'} ${
          isDisabled 
            ? 'opacity-50 cursor-not-allowed' 
            : 'border-green-300 text-green-700 hover:bg-green-50 hover:border-green-400'
        }`}
        title={getDisabledReason()}
        data-testid="message-client-btn"
      >
        <MessageSquare className={`${compact ? 'w-3 h-3' : 'w-4 h-4'} mr-1`} />
        <span className="hidden sm:inline">Message Client</span>
        <span className="sm:hidden">Message</span>
      </Button>

      {/* Message Composer Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-green-600" />
              Message Client
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Client Info */}
            <div className="bg-slate-50 p-3 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-slate-900">{job.client_name}</p>
                  <p className="text-sm text-slate-600">{job.client_phone}</p>
                </div>
                <Badge className="bg-yellow-100 text-yellow-800">
                  {job.job_type}
                </Badge>
              </div>
              <div className="mt-2 text-xs text-slate-500">
                {new Date(job.date).toLocaleDateString('en-GB', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })} at {job.time}
              </div>
            </div>

            {/* Missing Fields Warning */}
            {missingFields.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-red-800">Job details incomplete</p>
                    <p className="text-sm text-red-600 mt-1">
                      Missing: {missingFields.join(', ')}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Message Editor */}
            {missingFields.length === 0 && (
              <>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-slate-700">
                      Message Preview
                    </label>
                    <span className="text-xs text-slate-500 flex items-center gap-1">
                      <Edit3 className="w-3 h-3" />
                      Editable before sending
                    </span>
                  </div>
                  <Textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    className="min-h-[280px] text-sm font-normal leading-relaxed"
                    placeholder="Your message to the client..."
                    data-testid="message-textarea"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    {message.length} characters
                  </p>
                </div>

                {/* Send Method Selection */}
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">
                    Send via
                  </label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={sendMethod === 'sms' ? 'default' : 'outline'}
                      onClick={() => setSendMethod('sms')}
                      className={`flex-1 ${sendMethod === 'sms' ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
                      data-testid="send-method-sms"
                    >
                      <Smartphone className="w-4 h-4 mr-2" />
                      SMS
                    </Button>
                    <Button
                      type="button"
                      variant={sendMethod === 'whatsapp' ? 'default' : 'outline'}
                      onClick={() => setSendMethod('whatsapp')}
                      className={`flex-1 ${sendMethod === 'whatsapp' ? 'bg-green-600 hover:bg-green-700' : ''}`}
                      data-testid="send-method-whatsapp"
                    >
                      <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                      WhatsApp
                    </Button>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => setIsOpen(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={sendMethod === 'sms' ? sendViaSMS : sendViaWhatsApp}
                    className={`flex-1 ${
                      sendMethod === 'sms' 
                        ? 'bg-blue-600 hover:bg-blue-700' 
                        : 'bg-green-600 hover:bg-green-700'
                    }`}
                    disabled={!message.trim()}
                    data-testid="send-message-btn"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Open {sendMethod === 'sms' ? 'SMS' : 'WhatsApp'}
                  </Button>
                </div>

                <p className="text-xs text-center text-slate-500">
                  This will open your {sendMethod === 'sms' ? 'messaging app' : 'WhatsApp'} with the message pre-filled. 
                  You can review and edit before sending.
                </p>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MessageClientButton;
